<template>
  <ontos-base v-bind="baseProps" flush ref="base">
    <ontos-auth-layout :title="$tt('login-title')">
      <template #social>
        <ontos-google-sign-in @signed="enter()" @failed="onGoogleFailed($event)"/>
      </template>

      <v-form @submit.prevent="login()">
        <v-text-field v-model="email" :label="$tt('email')" type="email" autocomplete="email"
                      :error-messages="errorMessages.email"
                      @update:model-value="errorMessages.email = ''"/>

        <v-text-field v-model="password" :label="$tt('password')" type="password"
                      autocomplete="current-password" class="mt-2"
                      :error-messages="errorMessages.password"
                      @update:model-value="errorMessages.password = ''"/>

        <!-- Quem esquece a senha não tinha saída nenhuma: era preciso escrever para alguém.
             Junto do campo, que é onde a pessoa está quando percebe que esqueceu. -->
        <div class="d-flex justify-end">
          <v-btn variant="text" size="small" class="auth-link" @click="resetPassword()">
            {{ $tt('auth.forgot-password') }}
          </v-btn>
        </div>

        <v-btn type="submit" block color="primary" size="large" class="mt-4" :loading="isSaving">
          {{ $tt('login') }}
        </v-btn>
      </v-form>

      <!-- O reenvio da verificação deixou de ser automático e silencioso: ele acontecia dentro
           do erro, sem a pessoa pedir nem saber. Agora é um botão, e só aparece quando serve. -->
      <div v-if="unverified" class="mt-4">
        <v-btn variant="text" size="small" class="auth-link" :loading="isSaving"
               @click="resendVerification()">
          {{ $tt('auth.resend-verification') }}
        </v-btn>
      </div>

      <template #foot>
        <span>{{ $tt('no-account-message') }}</span>
        <router-link class="plain-link ms-1" :to="{name: 'signup'}">
          {{ $tt('request-invitation') }}
        </router-link>
      </template>
    </ontos-auth-layout>
  </ontos-base>
</template>

<script lang="ts">
import {Component, toNative} from 'vue-facing-decorator'
import {AuthorizationError, CredentialError, EmailNotVerifiedError, NotValidError} from "@/errors/errors";
import RouteComponent from "@/components/shared/RouteComponent";
import AuthLayout from "@/components/home/AuthLayout.vue";
import GoogleSignIn from "@/components/home/GoogleSignIn.vue";
import {AuthRefusal, takeRefusal} from "@/utils/authRefusal";

type Field = "email" | "password"

@Component({
  components: {
    'ontos-auth-layout': AuthLayout,
    'ontos-google-sign-in': GoogleSignIn
  }
})
class Login extends RouteComponent {
  email = ''
  password = ''
  /** O e-mail existe e a senha confere, mas falta verificar o endereço — só então há o que reenviar. */
  unverified = false
  errorMessages = {
    email: '',
    password: ''
  }

  /**
   * A recusa que o cadastro deixou, quando se veio de lá — ver o `Signup.onGoogleFailed`.
   *
   * No `load` porque é o que o chassi chama ao montar, e a entrega se consome uma vez: quem abre
   * o login pela URL, ou recarrega, não acha nada e a tela abre limpa.
   */
  load() {
    const refusal = takeRefusal()
    if (refusal) this.onGoogleFailed(refusal)
  }

  /** A recusa no campo de e-mail, **com o endereço que tentou entrar** — ver o `AuthRefusal`. */
  onGoogleFailed({message, email}: AuthRefusal) {
    if (email) this.email = email
    this.errorMessages.email = message
  }

  async enter() {
    this.info(this.$tt('info.login-success'))
    await this.$router.push({name: 'home'})
  }

  async login() {
    await this.saving(async () => {
      this.errorMessages.email = this.required(this.email)
      this.errorMessages.password = this.required(this.password)
      if (this.errorMessages.email || this.errorMessages.password) return
      try {
        this.unverified = false
        await this.$auth.login(this)
        await this.enter()
      } catch (err) {
        this.report(err)
      }
    })
  }

  /**
   * A recusa no campo a que ela pertence, e não num aviso solto.
   *
   * Cada erro sabe de qual campo ele fala: credencial inválida é da senha, convite pendente e
   * e-mail não verificado são do e-mail. Erro de campo num aviso vermelho faz procurar onde foi.
   */
  private report(err: any) {
    if (err instanceof CredentialError)
      this.errorMessages.password = this.$tt('auth.invalid-credentials') as string
    else if (err instanceof NotValidError)
      this.errorMessages[err.data.property as Field] =
          this.$tt('auth.invalid-field', {reason: err.data.validation}) as string
    else if (err instanceof AuthorizationError)
      this.errorMessages.email = this.$tt('auth.not-approved') as string
    else if (err instanceof EmailNotVerifiedError) {
      this.errorMessages.email = this.$tt('auth.email-not-verified') as string
      this.unverified = true
    }
    else throw err
  }

  /**
   * Manda o e-mail de redefinição para o endereço digitado.
   *
   * Precisa do campo preenchido, e é por isso que ele é conferido aqui: sem endereço não há para
   * onde mandar, e um botão que não faz nada é pior que um botão que explica.
   */
  async resetPassword() {
    this.errorMessages.email = this.required(this.email)
    if (this.errorMessages.email) return
    await this.saving(async () => {
      await this.$auth.sendPasswordReset(this.email)
      this.info(this.$tt('info.password-reset-sent', {email: this.email}))
    })
  }

  async resendVerification() {
    await this.saving(async () => {
      await this.$auth.sendEmailVerification()
      this.info(this.$tt('info.verification-sent'))
    })
  }
}

export default toNative(Login)
</script>

<style scoped>
/* Botão que é link: sem caixa alta e sem o recuo de botão, para ler como texto. Fica aqui, e não
   na casca, porque é desta tela — é ela que tem as duas saídas de senha. */
.auth-link {
  letter-spacing: normal;
  padding: 0 4px;
  text-transform: none;
}
</style>
